<template>
    <v-scroll-x-transition>
        <v-col
            align="center"
            v-show="
                submitLoading ||
                error.length > 0 ||
                isRateLimitError ||
                isRateLimitFinalError
            "
        >
            <div
                class="tw-text-center tw-text-xl tw-font-bold"
                v-if="showRateLimitLoading"
            >
                Booking your Spot
            </div>

            <div>
                <img
                    v-if="isRateLimitFinalError"
                    src="@/assets/queue.svg"
                    :height="dimensions.height"
                    :width="dimensions.width"
                />
                <lottie-animation
                    v-else
                    :path="image"
                    :height="dimensions.height"
                    :width="dimensions.width"
                />
            </div>

            <!-- Error Message -->

            <!-- Rate Limit Loading Error -->
            <div v-if="showRateLimitLoading">
                <p
                    v-if="isRateLimitError"
                    class="text--secondary tw-text-sm tw-italic"
                >
                    {{ $t('message.hangTight') }}
                </p>
                <p class="mt-2 tw-text-sm">{{ error }}</p>
            </div>

            <p v-if="submitLoading" class="tw-mt-2">
                {{ $t('message.bookingClass') + $t('message.ellipsis') }}
            </p>

            <!-- Final Rate Limit Error -->
            <div
                v-if="showFinalRateLimitError"
                class="tw-mt-2 tw-flex tw-flex-col tw-gap-2"
            >
                <p class="tw-text-xl tw-font-bold">
                    {{ $t('message.weAreReallySorry') }}
                </p>
                <div>
                    <p class="text--secondary">{{ error }}</p>
                </div>
            </div>

            <!-- Ordinary Error -->
            <div
                v-if="
                    error.length > 0 &&
                    !showFinalRateLimitError &&
                    !showRateLimitLoading
                "
                class="tw-mt-2 tw-flex tw-flex-col tw-gap-2"
            >
                <p class="tw-text-xl tw-font-bold">
                    {{ $t('message.weAreReallySorry') }}
                </p>
                <div>
                    <p class="text--secondary" v-if="numRetries > 0">
                        {{ $t('message.afterSeveralTries') }}
                    </p>
                    <p class="error--text">{{ error }}</p>
                </div>
            </div>

            <v-progress-circular
                v-if="showRateLimitLoading"
                class="tw-mt-4"
                indeterminate
                color="primary"
            />

            <v-btn
                v-else-if="!isRateLimitFinalError && error.length > 0"
                class="tw-mt-2 !tw-uppercase"
                text
                @click="closeDialog"
            >
                Close
            </v-btn>

            <div
                v-else-if="isRateLimitFinalError"
                class="tw-flex tw-flex-row tw-justify-center tw-gap-4 tw-mt-4"
            >
                <v-btn class="!tw-uppercase" @click="closeDialog">
                    Close
                </v-btn>
                <v-btn
                    class="!tw-uppercase tw-font-bold"
                    color="primary"
                    @click="bookClassFromCloud(0)"
                >
                    Try Again
                </v-btn>
            </div>
        </v-col>
    </v-scroll-x-transition>
</template>
<script>
import { mapGetters } from 'vuex'
import { STUDIO_MODULE_NAME } from '@/store/moduleNames'
import { bookClass } from '@/util/cloudFunctions/classesFunctions'
import { waitlistClass } from '@/util/cloudFunctions/classesFunctions'

// Analytics
import {
    BOOK_CLASS,
    BOOK_CLASS_ERROR,
    WAITLIST_CLASS,
    WAITLIST_CLASS_ERROR,
    WEBAPP_BOOK_CLASS,
    WEBAPP_BOOK_CLASS_ERROR,
    WEBAPP_WAITLIST_CLASS,
    WEBAPP_WAITLIST_CLASS_ERROR,
} from '@/analytics/events'
import { subStr } from '@/analytics/util'
import LottieAnimation from 'lottie-vuejs/src/LottieAnimation.vue' // import lottie-vuejs
import { RATE_LIMIT_ERROR } from '@/util/vibefamErrors'

export default {
    components: {
        LottieAnimation,
    },
    props: {
        hideSpots: Boolean,
        classId: String,
        spotNumbers: Array,
        numSpots: Number,
        packageId: String,
        isWaitlistClass: Boolean,
        selectedFamilyPackages: Array,
        bookingType: String,
    },
    data() {
        return {
            submitLoading: true,
            error: '',
            numRetries: 0,
            isRateLimitError: false,
            isRateLimitFinalError: false,
        }
    },
    computed: {
        dimensions() {
            if (this.isRateLimitError) {
                const width = this.$vuetify.breakpoint.mdAndUp ? 350 : 200
                const height = this.$vuetify.breakpoint.mdAndUp ? 200 : 120
                return { width, height }
            }

            if (this.isRateLimitFinalError) {
                const size = this.$vuetify.breakpoint.mdAndUp ? 300 : 180
                return { width: size, height: size }
            }

            if (this.error.length > 0) {
                const size = this.$vuetify.breakpoint.mdAndUp ? 200 : 120
                return { width: size, height: size }
            }

            const size = this.$vuetify.breakpoint.mdAndUp ? 350 : 200
            return { width: size, height: size }
        },
        image() {
            if (this.isRateLimitError) {
                return 'lottie/queue.json'
            }

            if (this.error.length > 0) {
                return `lottie/booking-failed.json`
            }
            const randomInt = Math.floor(Math.random() * 10 + 1)
            return `lottie/loading${randomInt}.json`
        },
        showRateLimitLoading() {
            return this.isRateLimitError
        },
        showFinalRateLimitError() {
            return (
                this.error.length > 0 &&
                this.isRateLimitFinalError &&
                !this.isRateLimitError
            )
        },
        ...mapGetters({
            studio: `${STUDIO_MODULE_NAME}/getStudio`,
        }),
    },
    methods: {
        async bookClassFromCloud(numRetries = 0) {
            this.numRetries = numRetries

            if (numRetries === 0) {
                // reset the error state
                this.isRateLimitError = false
                this.isRateLimitFinalError = false
                this.submitLoading = true
                this.error = ''

                this.$emit('start')
            }

            const accountBookings = []
            if (this.bookingType === 'family') {
                this.selectedFamilyPackages.forEach((pkg) => {
                    accountBookings.push({
                        packageId: pkg.packageId,
                        accountId: pkg.userId,
                    })
                })
            }

            const result = await bookClass(
                this.classId,
                this.spotNumbers,
                this.numSpots,
                this.packageId,
                this.bookingType,
                accountBookings,
                this.studio.id
            )
            if (result.success) {
                this.logSuccess(BOOK_CLASS)
                this.logSuccess(WEBAPP_BOOK_CLASS)
                this.$emit('success')

                this.error = ''
                this.isRateLimitError = false
            } else {
                this.$gtag.event(BOOK_CLASS_ERROR, {
                    errorMessage: subStr(result.error),
                })
                this.$gtag.event(WEBAPP_BOOK_CLASS_ERROR, {
                    errorMessage: subStr(result.error),
                })
                this.error = result.error

                if ('code' in result && result.code === RATE_LIMIT_ERROR) {
                    this.isRateLimitError = true
                } else {
                    this.isRateLimitError = false
                }
            }

            this.submitLoading = false

            if (this.isRateLimitError) {
                if (numRetries < 3) {
                    // wait 5 seconds before retrying
                    // the total wait time is 5 * 3 = 15 seconds
                    const jitter = this.getRandomBetween(0, 1000)
                    await new Promise((resolve) =>
                        setTimeout(resolve, 5000 + jitter)
                    )
                    await this.bookClassFromCloud(numRetries + 1)
                } else {
                    // after several retries, we stop retrying and show an error
                    this.error =
                        "There are a lot of booking activity at the moment, and we couldn't secure your spot this time. You can try again or explore other available classes."
                    this.isRateLimitError = false // to stop the loading in close button
                    this.isRateLimitFinalError = true // to show the final rate limit error see image()
                    this.$emit('end')
                }
            } else {
                this.$emit('end')
            }
        },
        getRandomBetween(min, max) {
            return Math.random() * (max - min) + min
        },
        async waitlistClassFromCloud() {
            this.submitLoading = true
            const result = await waitlistClass(this.classId, this.packageId)

            if (result.success) {
                this.logSuccess(WAITLIST_CLASS)
                this.logSuccess(WEBAPP_WAITLIST_CLASS)
                this.$emit('success')
            } else {
                this.$gtag.event(WAITLIST_CLASS_ERROR, {
                    errorMessage: subStr(result.error),
                })
                this.$gtag.event(WEBAPP_WAITLIST_CLASS_ERROR, {
                    errorMessage: subStr(result.error),
                })
                this.error = result.error
                return
            }
            this.submitLoading = false
        },
        logSuccess(event) {
            const eventData = {
                studioId: this.studio.id,
                studioName: this.studio.studioName,
                packageId: this.packageId,
                classId: this.classId,
            }
            this.$gtag.event(event, eventData)
        },
        closeDialog() {
            this.$emit('cancel')
        },
    },
    created() {
        if (this.isWaitlistClass) {
            this.waitlistClassFromCloud()
        } else {
            this.bookClassFromCloud()
        }
    },
}
</script>
<style scoped>
p {
    margin: 0;
}
</style>
